// src/features/portfolio-optimization/components/HoldingsTable.tsx
//
// Institutional "glass-box" holdings table for the active risk profile.
// Each row exposes WHY the optimiser took the position: the target weight
// (with an inline magnitude bar), the Phase-1 ML conviction signal (z-score
// badge, green = bullish / red = bearish) and the annualised volatility.
// Dark-mode aware and styled to match the existing ranking tables.

import { Link } from 'react-router-dom';

import { sectorColor } from './sectorColors';
import type { PortfolioAsset } from '../api';

interface HoldingsTableProps {
    holdings: PortfolioAsset[];
    /** Largest weight in the active profile — scales the magnitude bars. */
    maxWeight: number;
}

const signalBadgeClass = (signal: number): string => {
    if (signal > 1.0) {
        return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800';
    }
    if (signal < -1.0) {
        return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800';
    }
    return 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
};

const th =
    'px-5 py-3.5 font-semibold text-slate-400 dark:text-slate-500 uppercase text-xs tracking-wider';

export const HoldingsTable = ({ holdings, maxWeight }: HoldingsTableProps) => {
    if (!holdings.length) {
        return (
            <div className="p-10 text-center muted">
                No holdings cleared the dust threshold for this profile.
            </div>
        );
    }

    return (
        <div className="overflow-y-auto max-h-[560px]">
            <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-white dark:bg-slate-800 sticky top-0 z-10 shadow-sm border-b border-slate-200 dark:border-slate-700">
                    <tr>
                        <th className={th}>Asset</th>
                        <th className={th}>Sector</th>
                        <th className={`${th} text-right`}>Target Weight</th>
                        <th className={`${th} text-right`}>Volatility</th>
                        <th className={`${th} text-right`}>
                            <span className="inline-flex items-center justify-end gap-1">
                                <svg
                                    className="w-3 h-3 text-amber-500"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
                                </svg>
                                ML Signal (Z)
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                    {holdings.map((asset) => {
                        const pct = asset.target_weight * 100;
                        const barPct =
                            maxWeight > 0
                                ? Math.min((asset.target_weight / maxWeight) * 100, 100)
                                : 0;
                        return (
                            <tr
                                key={asset.symbol}
                                className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                            >
                                <td className="px-5 py-3">
                                    <Link
                                        to={`/dashboard/stock-info-page?symbol=${encodeURIComponent(asset.symbol)}`}
                                        title={`View ${asset.symbol} stock deep-dive`}
                                        className="font-bold text-indigo-600 dark:text-indigo-400 hover:underline text-base"
                                    >
                                        {asset.symbol}
                                    </Link>
                                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[200px]">
                                        {asset.company_name}
                                    </div>
                                </td>
                                <td className="px-5 py-3">
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded text-xs font-medium text-slate-600 dark:text-slate-300">
                                        <span
                                            className="w-2 h-2 rounded-full"
                                            style={{ backgroundColor: sectorColor(asset.sector) }}
                                        />
                                        {asset.sector}
                                    </span>
                                </td>
                                <td className="px-5 py-3 text-right">
                                    <div className="font-mono font-bold text-emerald-600 dark:text-emerald-400 text-base">
                                        {pct.toFixed(2)}%
                                    </div>
                                    <div className="w-full flex justify-end mt-1">
                                        <div className="w-20 bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden flex justify-end">
                                            <div
                                                className="bg-emerald-500 h-1.5 rounded-full"
                                                style={{ width: `${barPct}%` }}
                                            />
                                        </div>
                                    </div>
                                </td>
                                <td className="px-5 py-3 text-right font-mono text-slate-600 dark:text-slate-300">
                                    {asset.annualized_volatility !== null
                                        ? `${(asset.annualized_volatility * 100).toFixed(1)}%`
                                        : '—'}
                                </td>
                                <td className="px-5 py-3 text-right">
                                    {asset.forge_ultra_signal !== null ? (
                                        <span
                                            className={`inline-flex px-2 py-1 rounded text-xs font-bold border ${signalBadgeClass(
                                                asset.forge_ultra_signal,
                                            )}`}
                                        >
                                            {asset.forge_ultra_signal > 0 ? '+' : ''}
                                            {asset.forge_ultra_signal.toFixed(2)}σ
                                        </span>
                                    ) : (
                                        <span className="text-slate-300 dark:text-slate-600 font-mono">
                                            —
                                        </span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default HoldingsTable;
